import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { differenceInCalendarDays, format } from "date-fns";
import {
  assignDevice,
  fetchAssignedDevice,
  fetchAssignmentHistory,
  fetchOrgPlaces,
  fetchAssetHistory,
  fetchSurplusDevices,
  recallDevice,
  renameDeviceAsset,
} from "utils/orgApi";
import { readUser } from "utils/session";

const HISTORY_MAX_DAYS = 31;

const fieldClass =
  "rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-navy-700 dark:border-white/10 dark:bg-navy-900 dark:text-white";

function sessionCustomers() {
  const user = readUser();
  const customerId = user?.access?.customerId;
  if (customerId == null || customerId === "") return [];
  return [{ customerId, name: user.customerName || "This customer" }];
}

function actorFields() {
  const user = readUser() || {};
  return {
    employeeId: user.userid ?? user.employee_id ?? null,
    actorName: user.username || user.displayName || user.display_name || "",
  };
}

function Field({ label, children }) {
  return (
    <label className="flex min-w-[10rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
      {children}
    </label>
  );
}

function LoadButton({ busy, onClick, children = "Load" }) {
  return (
    <button
      type="button"
      className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
      disabled={busy}
      onClick={onClick}
    >
      {busy ? "Loading…" : children}
    </button>
  );
}

function ClearButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-navy-700 disabled:opacity-50 dark:border-white/10 dark:text-white"
      disabled={disabled}
      onClick={onClick}
    >
      Clear
    </button>
  );
}

function AssetIdEditor({ device, busy, onSave }) {
  const [value, setValue] = useState(device.aid || "");
  useEffect(() => {
    setValue(device.aid || "");
  }, [device.aid, device.did]);
  return (
    <span className="mr-2 inline-flex items-center gap-2">
      <input
        className={`${fieldClass} w-40`}
        value={value}
        maxLength={40}
        placeholder={`AST${device.did}`}
        aria-label={`Asset id for device ${device.did}`}
        onChange={(event) => setValue(event.target.value)}
      />
      <button
        type="button"
        className="rounded-md border border-gray-300 px-3 py-1 text-navy-700 disabled:opacity-50 dark:text-white"
        disabled={busy}
        onClick={() => onSave(device.did, value)}
      >
        Save
      </button>
    </span>
  );
}

function Section({ title, hint, children }) {
  return (
    <section className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
      <h3 className="text-lg font-bold text-navy-700 dark:text-white">{title}</h3>
      <p className="mt-1 text-sm text-gray-600">{hint}</p>
      {children}
    </section>
  );
}

export default function DeviceTab({ onChanged }) {
  const [places, setPlaces] = useState(() => ({
    zones: [],
    branches: [],
    customers: sessionCustomers(),
  }));
  const [placesError, setPlacesError] = useState("");
  const [surplus, setSurplus] = useState(null);
  const [surplusBusy, setSurplusBusy] = useState(false);
  const [surplusError, setSurplusError] = useState("");
  const [zoneChoice, setZoneChoice] = useState({});
  const [branchChoice, setBranchChoice] = useState({});
  const [didQuery, setDidQuery] = useState("");
  const [assigned, setAssigned] = useState(null);
  const [assignedBusy, setAssignedBusy] = useState(false);
  const [assignedError, setAssignedError] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [customerId, setCustomerId] = useState(() => {
    const id = readUser()?.access?.customerId;
    return id != null && id !== "" ? String(id) : "";
  });
  const [history, setHistory] = useState(null);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [assetFromDate, setAssetFromDate] = useState(null);
  const [assetToDate, setAssetToDate] = useState(null);
  const [assetHistory, setAssetHistory] = useState(null);
  const [assetHistoryBusy, setAssetHistoryBusy] = useState(false);
  const [assetHistoryError, setAssetHistoryError] = useState("");
  const [busyDid, setBusyDid] = useState(null);

  const ensurePlaces = async () => {
    if (places.zones.length) return places;
    const catalog = await fetchOrgPlaces();
    setPlaces(catalog);
    if (!customerId && catalog.customers?.[0]?.customerId != null) {
      setCustomerId(String(catalog.customers[0].customerId));
    }
    return catalog;
  };

  const zones = places.zones || [];
  const customers = places.customers || [];
  const branchesByZone = useMemo(() => {
    const grouped = {};
    (places.branches || []).forEach((branch) => {
      const key = String(branch.zoneId);
      grouped[key] = grouped[key] || [];
      grouped[key].push(branch);
    });
    return grouped;
  }, [places]);

  const loadSurplus = async () => {
    setSurplusBusy(true);
    setSurplusError("");
    setPlacesError("");
    try {
      const result = await fetchSurplusDevices();
      setSurplus(result.surplus);
      try {
        if (result.places?.zones?.length) setPlaces(result.places);
        else if (!places.zones.length) await ensurePlaces();
      } catch (err) {
        setPlacesError(err.message || "Could not load zones and branches.");
      }
    } catch (err) {
      setSurplusError(err.message || "Could not load surplus devices.");
    } finally {
      setSurplusBusy(false);
    }
  };

  const loadAssigned = async () => {
    const did = didQuery.trim();
    if (!/^\d+$/.test(did) || Number(did) <= 0) {
      setAssignedError("Enter a device id, then Load.");
      setAssigned(null);
      return;
    }
    setAssignedBusy(true);
    setAssignedError("");
    try {
      setAssigned(await fetchAssignedDevice(did));
    } catch (err) {
      setAssigned(null);
      setAssignedError(err.message || "Could not load the assigned device.");
    } finally {
      setAssignedBusy(false);
    }
  };

  const clearAssigned = () => {
    setDidQuery("");
    setAssigned(null);
    setAssignedError("");
  };

  const loadHistory = async () => {
    if (!fromDate || !toDate) {
      setHistoryError("Choose a from date and a to date.");
      return;
    }
    if (toDate < fromDate) {
      setHistoryError("To date is before from date.");
      return;
    }
    if (differenceInCalendarDays(toDate, fromDate) > HISTORY_MAX_DAYS) {
      setHistoryError(`Date range cannot be longer than ${HISTORY_MAX_DAYS} days.`);
      return;
    }
    if (!customerId) {
      setHistoryError("Select a customer.");
      return;
    }
    setHistoryBusy(true);
    setHistoryError("");
    try {
      setHistory(
        await fetchAssignmentHistory({
          from: format(fromDate, "yyyy-MM-dd"),
          to: format(toDate, "yyyy-MM-dd"),
          customerId,
        })
      );
    } catch (err) {
      setHistory(null);
      setHistoryError(err.message || "Could not load assignment history.");
    } finally {
      setHistoryBusy(false);
    }
  };

  const clearHistory = () => {
    const first = customers[0]?.customerId;
    setFromDate(null);
    setToDate(null);
    setCustomerId(first != null ? String(first) : "");
    setHistory(null);
    setHistoryError("");
  };

  const loadAssetHistory = async () => {
    if (!assetFromDate || !assetToDate) {
      setAssetHistoryError("Choose a from date and a to date.");
      return;
    }
    if (assetToDate < assetFromDate) {
      setAssetHistoryError("To date is before from date.");
      return;
    }
    if (differenceInCalendarDays(assetToDate, assetFromDate) > HISTORY_MAX_DAYS) {
      setAssetHistoryError(`Date range cannot be longer than ${HISTORY_MAX_DAYS} days.`);
      return;
    }
    if (!customerId) {
      setAssetHistoryError("Select a customer.");
      return;
    }
    setAssetHistoryBusy(true);
    setAssetHistoryError("");
    try {
      setAssetHistory(
        await fetchAssetHistory({
          from: format(assetFromDate, "yyyy-MM-dd"),
          to: format(assetToDate, "yyyy-MM-dd"),
          customerId,
        })
      );
    } catch (err) {
      setAssetHistory(null);
      setAssetHistoryError(err.message || "Could not load asset id history.");
    } finally {
      setAssetHistoryBusy(false);
    }
  };

  const clearAssetHistory = () => {
    setAssetFromDate(null);
    setAssetToDate(null);
    setAssetHistory(null);
    setAssetHistoryError("");
  };

  const saveAsset = async (did, aid, source) => {
    setBusyDid(did);
    setSurplusError("");
    setAssignedError("");
    try {
      await renameDeviceAsset({ did, aid });
      if (source === "surplus") await loadSurplus();
      else if (/^\d+$/.test(didQuery.trim())) await loadAssigned();
      onChanged?.();
    } catch (err) {
      const message = err.message || "The asset id was not saved.";
      if (source === "surplus") setSurplusError(message);
      else setAssignedError(message);
    } finally {
      setBusyDid(null);
    }
  };

  const run = async (did, action) => {
    setBusyDid(did);
    setSurplusError("");
    setAssignedError("");
    try {
      if (action === "assign") {
        const branchId = branchChoice[did];
        if (!zoneChoice[did] || !branchId) {
          setSurplusError("Select a zone and a branch before assigning.");
          return;
        }
        await assignDevice({ did, branchId: Number(branchId), ...actorFields() });
        await loadSurplus();
      } else {
        await recallDevice({ did, ...actorFields() });
        if (/^\d+$/.test(didQuery.trim())) await loadAssigned();
      }
      onChanged?.();
    } catch (err) {
      const message = err.message || "The change was not saved.";
      if (action === "assign") setSurplusError(message);
      else setAssignedError(message);
    } finally {
      setBusyDid(null);
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {placesError && <p className="text-sm text-red-500">{placesError}</p>}

      <Section
        title="Surplus devices"
        hint="Nothing is loaded until you press Load. Pick a zone, then a branch under that zone, and assign."
      >
        <div className="mt-3">
          <LoadButton busy={surplusBusy} onClick={loadSurplus} />
        </div>
        {surplusError && <p className="mt-3 text-sm text-red-500">{surplusError}</p>}
        {surplus == null ? (
          <p className="mt-3 text-sm text-gray-500">Surplus devices are not loaded.</p>
        ) : surplus.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No surplus devices.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Asset</th>
                  <th className="py-2 pr-4 font-semibold">DID</th>
                  <th className="py-2 pr-4 font-semibold">Zone</th>
                  <th className="py-2 pr-4 font-semibold">Branch</th>
                  <th className="py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {surplus.map((device) => {
                  const zoneId = zoneChoice[device.did] || "";
                  const zoneBranches = branchesByZone[zoneId] || [];
                  return (
                    <tr key={device.did} className="border-t border-gray-200 dark:border-white/10">
                      <td className="py-2 pr-4 text-navy-700 dark:text-white">{device.aid || "—"}</td>
                      <td className="py-2 pr-4">{device.did}</td>
                      <td className="py-2 pr-4">
                        <select
                          className={fieldClass}
                          value={zoneId}
                          onChange={(event) => {
                            const nextZone = event.target.value;
                            setZoneChoice((current) => ({ ...current, [device.did]: nextZone }));
                            setBranchChoice((current) => ({ ...current, [device.did]: "" }));
                          }}
                        >
                          <option value="">Select zone</option>
                          {zones.map((zone) => (
                            <option key={zone.zoneId} value={zone.zoneId}>
                              {zone.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pr-4">
                        <select
                          className={fieldClass}
                          value={branchChoice[device.did] || ""}
                          disabled={!zoneId}
                          onChange={(event) =>
                            setBranchChoice((current) => ({ ...current, [device.did]: event.target.value }))
                          }
                        >
                          <option value="">{zoneId ? "Select branch" : "Select a zone first"}</option>
                          {zoneBranches.map((branch) => (
                            <option key={branch.branchId} value={branch.branchId}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <AssetIdEditor
                          device={device}
                          busy={busyDid === device.did}
                          onSave={(did, aid) => saveAsset(did, aid, "surplus")}
                        />
                        <button
                          type="button"
                          className="rounded-md bg-brand-500 px-3 py-1 text-white disabled:opacity-50"
                          disabled={busyDid === device.did}
                          onClick={() => run(device.did, "assign")}
                        >
                          Assign
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Assigned devices"
        hint="Load one device by its id. The list stays empty until you search."
      >
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="Device id">
            <input
              className={fieldClass}
              inputMode="numeric"
              value={didQuery}
              placeholder="DID"
              onChange={(event) => setDidQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") loadAssigned();
              }}
            />
          </Field>
          <LoadButton busy={assignedBusy} onClick={loadAssigned} />
          <ClearButton onClick={clearAssigned} />
        </div>
        {assignedError && <p className="mt-3 text-sm text-red-500">{assignedError}</p>}
        {assigned == null ? (
          <p className="mt-3 text-sm text-gray-500">No device loaded.</p>
        ) : assigned.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No assigned device with that id.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-2 pr-4 font-semibold">Asset</th>
                  <th className="py-2 pr-4 font-semibold">DID</th>
                  <th className="py-2 pr-4 font-semibold">Zone</th>
                  <th className="py-2 pr-4 font-semibold">Branch</th>
                  <th className="py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {assigned.map((device) => (
                  <tr key={device.did} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-2 pr-4 text-navy-700 dark:text-white">{device.aid || "—"}</td>
                    <td className="py-2 pr-4">{device.did}</td>
                    <td className="py-2 pr-4">{device.zoneName || "—"}</td>
                    <td className="py-2 pr-4">{device.branchName || "—"}</td>
                    <td className="py-2 whitespace-nowrap">
                      <AssetIdEditor
                        device={device}
                        busy={busyDid === device.did}
                        onSave={(did, aid) => saveAsset(did, aid, "assigned")}
                      />
                      <button
                        type="button"
                        className="rounded-md border border-gray-300 px-3 py-1 text-navy-700 disabled:opacity-50 dark:text-white"
                        disabled={busyDid === device.did}
                        onClick={() => run(device.did, "recall")}
                      >
                        Recall
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Assignment history"
        hint={`Load a date range of at most ${HISTORY_MAX_DAYS} days for one customer. History stays empty until you press Load.`}
      >
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="From">
            <DatePicker
              selected={fromDate}
              onChange={setFromDate}
              className={fieldClass}
              placeholderText="From date"
              dateFormat="yyyy/MM/dd"
              maxDate={toDate || new Date()}
            />
          </Field>
          <Field label="To">
            <DatePicker
              selected={toDate}
              onChange={setToDate}
              className={fieldClass}
              placeholderText="To date"
              dateFormat="yyyy/MM/dd"
              minDate={fromDate}
              maxDate={new Date()}
            />
          </Field>
          <Field label="Customer">
            <select
              className={fieldClass}
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
            >
              <option value="">Select customer</option>
              {customers.map((customer) => (
                <option key={customer.customerId} value={customer.customerId}>
                  {customer.name || `Customer ${customer.customerId}`}
                </option>
              ))}
            </select>
          </Field>
          <LoadButton busy={historyBusy} onClick={loadHistory} />
          <ClearButton onClick={clearHistory} disabled={historyBusy} />
        </div>
        {historyError && <p className="mt-3 text-sm text-red-500">{historyError}</p>}
        {history == null ? (
          <p className="mt-3 text-sm text-gray-500">Assignment history is not loaded.</p>
        ) : history.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No assignment changes in this range.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-2 pr-4 font-semibold">When</th>
                  <th className="py-2 pr-4 font-semibold">Action</th>
                  <th className="py-2 pr-4 font-semibold">Device</th>
                  <th className="py-2 pr-4 font-semibold">From</th>
                  <th className="py-2 pr-4 font-semibold">To</th>
                  <th className="py-2 font-semibold">Changed by</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.logId} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-2 pr-4">{row.createdAt}</td>
                    <td className="py-2 pr-4">{row.action}</td>
                    <td className="py-2 pr-4">{row.aid || row.did}</td>
                    <td className="py-2 pr-4">{row.fromLabel}</td>
                    <td className="py-2 pr-4">{row.toLabel}</td>
                    <td className="py-2">{row.employeeName || row.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section
        title="Asset id history"
        hint={`Load a date range of at most ${HISTORY_MAX_DAYS} days. Each saved asset id is listed here. History stays empty until you press Load.`}
      >
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <Field label="From">
            <DatePicker
              selected={assetFromDate}
              onChange={setAssetFromDate}
              className={fieldClass}
              placeholderText="From date"
              dateFormat="yyyy/MM/dd"
              maxDate={assetToDate || new Date()}
            />
          </Field>
          <Field label="To">
            <DatePicker
              selected={assetToDate}
              onChange={setAssetToDate}
              className={fieldClass}
              placeholderText="To date"
              dateFormat="yyyy/MM/dd"
              minDate={assetFromDate}
              maxDate={new Date()}
            />
          </Field>
          <LoadButton busy={assetHistoryBusy} onClick={loadAssetHistory} />
          <ClearButton onClick={clearAssetHistory} disabled={assetHistoryBusy} />
        </div>
        {assetHistoryError && <p className="mt-3 text-sm text-red-500">{assetHistoryError}</p>}
        {assetHistory == null ? (
          <p className="mt-3 text-sm text-gray-500">Asset id history is not loaded.</p>
        ) : assetHistory.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">No asset id changes in this range.</p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-gray-500">
                  <th className="py-2 pr-4 font-semibold">When</th>
                  <th className="py-2 pr-4 font-semibold">DID</th>
                  <th className="py-2 pr-4 font-semibold">From</th>
                  <th className="py-2 pr-4 font-semibold">To</th>
                  <th className="py-2 font-semibold">Changed by</th>
                </tr>
              </thead>
              <tbody>
                {assetHistory.map((row) => (
                  <tr key={row.logId} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-2 pr-4">{row.createdAt}</td>
                    <td className="py-2 pr-4">{row.did}</td>
                    <td className="py-2 pr-4">{row.previousAid || "—"}</td>
                    <td className="py-2 pr-4">{row.newAid || "—"}</td>
                    <td className="py-2">{row.actorName || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
